import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateNavbarHeading } from "../../../../constants/layout/store/filterSlice";
import { GetServiceCaller } from "../../../../services/serviceCaller";
import { API_URL } from "../../../../constants";
import Select from "react-select";
import SearchBar from "../../../../components/SearchBar";
import Pagination from "../../../../components/Pagination";
import moment from "moment/moment";
import { Table } from "react-bootstrap";
import { FaSpinner } from "react-icons/fa";

const AllocateTaskList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [allocateTaskListData, setAllocateTaskListData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(150);
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("0");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [isSuperAdminFlag, setIsSuperAdminFlag] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [apiStatus, setApiStatus] = useState(undefined);
  const [hoveredProject, setHoveredProject] = useState(null);

  useEffect(() => {
    dispatch(updateNavbarHeading("Allocate Task"));
  }, [dispatch]);

  const fetchMyTaskListData = async () => {
    setApiStatus(undefined);
    try {
      setLoading(true);
      const response = await GetServiceCaller(
        `${API_URL}projectmanagement/task/task-allocation?P_EmployeeID=&P_ProjectID=`
      );
      setApiStatus(response?.status);
      if (response.status === 200) {
        const cleanedData = response?.data?.data;
        setIsSuperAdminFlag(cleanedData?.isSuperAdmin);

        if (cleanedData?.isSuperAdmin !== 1) {
          const empOpt = cleanedData?.employeeList?.map((o) => ({
            value: o.value,
            label: o.text,
          }));
          setEmployeeOptions(empOpt);
        } else {
          const projectOpt = cleanedData?.prj?.map((o) => ({
            value: o.value,
            label: o.text,
          }));
          setProjectOptions(projectOpt);
        }

        const taskData = cleanedData?.data || [];
        setAllocateTaskListData(taskData);

        // Set all groups expanded by default
        const grouped = taskData.reduce((acc, item) => {
          if (!acc[item.Project]) acc[item.Project] = true;
          return acc;
        }, {});
        setExpandedGroups(grouped);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpNameData = async (projectID) => {
    try {
      setLoading(true);
      const response = await GetServiceCaller(
        `${API_URL}projectmanagement/task/task-allocation/getEmployeeName?ProjectID=${projectID}`
      );

      if (response.status === 200) {
        const empOpt = response?.data?.data?.data?.map((o) => ({
          value: o.value,
          label: o.text,
        }));
        setEmployeeOptions(empOpt);
      }
    } catch (error) {
      console.error("Error fetching employee data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectNameData = async (empID) => {
    try {
      setLoading(true);
      const response = await GetServiceCaller(
        `${API_URL}projectmanagement/task/task-allocation/getProjectName?EmployeeID=${empID}`
      );

      if (response.status === 200) {
        const prjOpt = response?.data?.data?.map((o) => ({
          value: o.value,
          label: o.text,
        }));
        setProjectOptions(prjOpt);
      }
    } catch (error) {
      console.error("Error fetching project data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async (projectId, selectedEmployee) => {
    setApiStatus(undefined);
    try {
      setLoading(true);
      const response = await GetServiceCaller(
        `${API_URL}projectmanagement/task/task-allocation?P_EmployeeID=${selectedEmployee}&P_ProjectID=${projectId}`
      );
      setApiStatus(response?.status);
      if (response.status === 200) {
        const taskData = response?.data?.data?.data || [];
        setAllocateTaskListData(taskData);

        // Expand all groups again after filter
        const grouped = taskData.reduce((acc, item) => {
          if (!acc[item.Project]) acc[item.Project] = true;
          return acc;
        }, {});
        setExpandedGroups(grouped);
      }
    } catch (error) {
      console.error("Error fetching filtered tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyTaskListData();
  }, []);

  useEffect(() => {
    if (selectedProject && isSuperAdminFlag === 1) {
      fetchEmpNameData(selectedProject);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedEmployee !== "0" && isSuperAdminFlag === 0) {
      fetchProjectNameData(selectedEmployee);
    }
  }, [selectedEmployee]);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleAddNew = () => navigate("/allocate-task/add");
  const handleEditbtn = (id) => navigate(`/allocate-task/edit/${id.ID}`);

  const toggleGroup = (projectName) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [projectName]: !prev[projectName],
    }));
  };

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return allocateTaskListData.slice(startIndex, startIndex + itemsPerPage);
  }, [allocateTaskListData, currentPage, itemsPerPage]);

  const filteredData = useMemo(() => {
    return paginatedData.filter((item) =>
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [paginatedData, searchTerm]);

  const groupedData = useMemo(() => {
    return filteredData.reduce((acc, item) => {
      if (!acc[item.Project]) acc[item.Project] = [];
      acc[item.Project].push(item);
      return acc;
    }, {});
  }, [filteredData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="container-fluid" id="skill_section">
      <div id="skill-table-area">
        <div>
          <div className="row align-items-end mb-2 gy-3">
            {isSuperAdminFlag === 1 ? (
              <>
                <div className="col-md-2">
                  <label htmlFor="projectSelect" className="form-label">
                    Project
                  </label>
                  <Select
                    name="projects"
                    inputId="projectSelect"
                    onChange={(e) => setSelectedProject(e?.value || "")}
                    options={projectOptions}
                    value={
                      projectOptions?.find(
                        (o) => o.value === selectedProject
                      ) || null
                    }
                    placeholder="Select..."
                  />
                </div>
                <div className="col-md-2">
                  <label htmlFor="employeeSelect" className="form-label">
                    Employee
                  </label>
                  <Select
                    name="employee"
                    inputId="employeeSelect"
                    onChange={(e) => setSelectedEmployee(e?.value || "0")}
                    options={employeeOptions}
                    value={
                      employeeOptions?.find(
                        (o) => o.value === selectedEmployee
                      ) || null
                    }
                    placeholder="Select..."
                  />
                </div>
              </>
            ) : (
              <>
                <div className="col-md-2">
                  <label htmlFor="employeeSelect" className="form-label">
                    Employee
                  </label>
                  <Select
                    name="employee"
                    onChange={(e) => setSelectedEmployee(e?.value || "0")}
                    options={employeeOptions}
                    value={
                      employeeOptions?.find(
                        (o) => o.value === selectedEmployee
                      ) || null
                    }
                    placeholder="Select..."
                  />
                </div>
                <div className="col-md-2">
                  <label htmlFor="projectSelect" className="form-label">
                    Project
                  </label>
                  <Select
                    name="projects"
                    onChange={(e) => setSelectedProject(e?.value || "")}
                    options={projectOptions}
                    value={
                      projectOptions?.find(
                        (o) => o.value === selectedProject
                      ) || null
                    }
                    placeholder="Select..."
                  />
                </div>
              </>
            )}

            <div className="col-md-2">
              <div className="text-end text-sm-start">
              <button
                type="button"
                className="custom-btn"
                onClick={() => handleFilter(selectedProject, selectedEmployee)}
              >
                Get Data
              </button>
              </div>
            </div>

            <div className="col-md-6 d-flex justify-content-end">
              <div className="me-2">
                <SearchBar
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
              </div>
              <div>
                <button
                  id="Add-btn"
                  className="custom-btn"
                  onClick={handleAddNew}
                >
                  <i className="fa fa-plus"></i> Add
                </button>
              </div>
            </div>
          </div>

          <Table
            id="TaskAllocationIndex"
            striped
            bordered
            hover
            className="table-no-record-msg"
          >
            <thead id="thcolor">
              <tr>
                <th className="text-center">Task</th>
                <th className="text-center">From</th>
                <th className="text-center">To</th>
                <th className="text-center">Assigned To</th>
                <th className="text-center">Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {apiStatus === 200 &&
              groupedData &&
              Object.keys(groupedData).length > 0 ? (
                Object.entries(groupedData).map(([projectName, items]) => {
                  const isExpanded = expandedGroups[projectName];
                  const isHovered = hoveredProject === projectName;

                  return (
                    <React.Fragment key={projectName}>
                      <tr style={{ backgroundColor: "#DFEBFF" }}>
                        <td
                          colSpan={7}
                          onClick={() => toggleGroup(projectName)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: isHovered ? "#2e2e47" : "inherit",
                            color: isHovered ? "white" : "inherit",
                          }}
                        >
                          <span className="float-end me-2">
                            <i
                              className={`fa ${
                                isExpanded ? "fa-minus" : "fa-plus"
                              }`}
                            ></i>
                          </span>
                          <strong>{projectName}</strong>
                        </td>
                      </tr>
                      {isExpanded &&
                        items.map((data, index) => (
                          <tr key={index}>
                            <td className="text-left">{data.Name}</td>
                            <td className="text-center">
                              {data.StartDate
                                ? moment(data.StartDate).format("DD-MMM-YYYY")
                                : "-"}
                            </td>
                            <td className="text-center">
                              {data.EndDate
                                ? moment(data.EndDate).format("DD-MMM-YYYY")
                                : "-"}
                            </td>
                            <td className="text-left">{data.AssignedTo}</td>
                            <td className="text-left">{data.Status}</td>
                            <td className="text-center">
                              <a
                                href={`/allocate-task/edit/${data.ID}`}
                                title="Edit"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleEditbtn(data);
                                }}
                              >
                                <i className="bi bi-pencil-square pad-r-5 icons"></i>
                              </a>
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })
              ) : apiStatus === 200 ? (
                <tr>
                  <td colSpan={7} className="text-center">
                    No Data Found
                  </td>
                </tr>
              ) : apiStatus === undefined ? (
                <tr>
                  <td colSpan={7} className="text-center">
                    <FaSpinner className="spinner" />
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>

          <div className="d-flex justify-content-end">
            <Pagination
              itemsPerPage={itemsPerPage}
              totalItems={allocateTaskListData.length}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllocateTaskList;
